import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { isAdminLevel } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Archive, RotateCcw, Trash2, Search, Filter } from 'lucide-react';

const fmtCurrency = (v) => `\u20B9${(v || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

const TYPE_COLORS = {
  SE: 'bg-blue-100 text-blue-700',
  SR: 'bg-red-100 text-red-700',
  SO: 'bg-purple-100 text-purple-700',
  PE: 'bg-emerald-100 text-emerald-700',
  PR: 'bg-amber-100 text-amber-700',
  PO: 'bg-cyan-100 text-cyan-700',
  PRODUCT: 'bg-pink-100 text-pink-700',
  CUSTOMER: 'bg-indigo-100 text-indigo-700',
  AGENT: 'bg-teal-100 text-teal-700',
  VENDOR: 'bg-orange-100 text-orange-700',
};

const TYPE_LABELS = {
  SE: 'Sales Entry',
  SR: 'Sales Return',
  SO: 'Sales Order',
  PE: 'Purchase Entry',
  PR: 'Purchase Return',
  PO: 'Purchase Order',
  PRODUCT: 'Product',
  CUSTOMER: 'Customer',
  AGENT: 'Agent',
  VENDOR: 'Vendor',
};

const ArchivePage = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/archive/items?page=${page}&limit=50`;
      if (activeFilter) url += `&doc_type=${activeFilter}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      const res = await api.get(url);
      setItems(res.data.items);
      setTotal(res.data.total);
      setTypeCounts(res.data.type_counts || {});
    } catch {
      toast.error('Failed to load archive');
    } finally { setLoading(false); }
  }, [page, activeFilter, searchTerm]);

  useEffect(() => { if (isAdminLevel(user?.role)) loadItems(); }, [loadItems, user?.role]);

  const handleRestore = async (item) => {
    setActionLoading(true);
    try {
      await api.post(`/archive/restore/${item.id}`);
      toast.success(`${item._archive_type} ${item._display_number || item.id} restored`);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally { setActionLoading(false); setConfirmDialog(null); }
  };

  const handlePermanentDelete = async (item) => {
    setActionLoading(true);
    try {
      await api.delete(`/archive/permanent-delete/${item.id}`);
      toast.success(`${item._archive_type} ${item._display_number || item.id} permanently deleted`);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally { setActionLoading(false); setConfirmDialog(null); }
  };

  if (!isAdminLevel(user?.role)) {
    return <div className="p-8 text-center text-gray-500">Admin access required</div>;
  }

  const totalArchived = Object.values(typeCounts).reduce((s, c) => s + c, 0);
  const filterTypes = ['SE', 'SR', 'SO', 'PE', 'PR', 'PO', 'PRODUCT', 'CUSTOMER', 'AGENT', 'VENDOR'];

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="archive-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Archive</h1>
          <p className="text-gray-600 mt-1">Deleted transactions · Restore or permanently delete</p>
        </div>
        <Badge className="bg-slate-100 text-slate-700 text-sm px-3 py-1">{totalArchived} archived</Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <Button size="sm" variant={activeFilter === null ? 'default' : 'outline'}
              className={activeFilter === null ? 'bg-slate-800' : ''} onClick={() => { setActiveFilter(null); setPage(1); }}
              data-testid="filter-all">
              All ({totalArchived})
            </Button>
            {filterTypes.map(t => (
              <Button key={t} size="sm" variant={activeFilter === t ? 'default' : 'outline'}
                className={activeFilter === t ? 'bg-slate-800' : ''}
                onClick={() => { setActiveFilter(t); setPage(1); }}
                data-testid={`filter-${t}`}>
                {t} ({typeCounts[t] || 0})
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input placeholder="Search by entry number, invoice..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
              className="max-w-sm h-9" data-testid="archive-search" />
          </div>
        </CardContent>
      </Card>

      {/* Archive Table */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700"></div></div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-400 text-lg">Archive is empty</p>
            <p className="text-gray-400 text-sm mt-1">Deleted transactions will appear here</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="archive-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-medium">Type</th>
                    <th className="px-4 py-3 text-left font-medium">Number</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-right font-medium">Amount</th>
                    <th className="px-4 py-3 text-left font-medium">Archived By</th>
                    <th className="px-4 py-3 text-left font-medium">Archived At</th>
                    <th className="px-4 py-3 text-center font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`archive-row-${item.id}`}>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs ${TYPE_COLORS[item._archive_type] || 'bg-gray-100 text-gray-700'}`}>
                          {item._archive_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono font-medium text-gray-800">{item._display_number || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{fmtDate(item._display_date)}</td>
                      <td className="px-4 py-3 text-right font-medium">{fmtCurrency(item._display_amount)}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{item._archived_by_name || '-'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(item._archived_at)}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="outline" className="gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50 h-7 text-xs"
                            disabled={actionLoading}
                            onClick={() => setConfirmDialog({ action: 'restore', item })}
                            data-testid={`restore-${item.id}`}>
                            <RotateCcw className="w-3 h-3" /> Restore
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs"
                            disabled={actionLoading}
                            onClick={() => setConfirmDialog({ action: 'delete', item })}
                            data-testid={`perm-delete-${item.id}`}>
                            <Trash2 className="w-3 h-3" /> Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {total > 50 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <span className="text-xs text-gray-500">Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <Button size="sm" variant="outline" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog?.action === 'restore' ? 'Restore Transaction' : 'Permanently Delete'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog?.action === 'restore'
                ? `Restore ${TYPE_LABELS[confirmDialog?.item?._archive_type] || ''} ${confirmDialog?.item?._display_number || ''} back to active? Stock will be recalculated.`
                : `Permanently delete ${TYPE_LABELS[confirmDialog?.item?._archive_type] || ''} ${confirmDialog?.item?._display_number || ''}? This cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDialog(null)} disabled={actionLoading}>Cancel</Button>
            {confirmDialog?.action === 'restore' ? (
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={actionLoading}
                onClick={() => handleRestore(confirmDialog.item)} data-testid="confirm-restore">
                {actionLoading ? 'Restoring...' : 'Restore'}
              </Button>
            ) : (
              <Button className="bg-red-600 hover:bg-red-700" disabled={actionLoading}
                onClick={() => handlePermanentDelete(confirmDialog.item)} data-testid="confirm-delete">
                {actionLoading ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchivePage;
